import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { ErrorProperties } from '../../API/identityAPIProperties';
import API from '../../API/Main/API';
import { resetState } from '../../resetState';
import { BranchProperties, ClientBranchProperties } from '../../API/ClientAPIHelpers/branchProperties';

export const getBranches = createAsyncThunk('client/branches/all', async (_, thunkAPI) => {
	const response = await API.ClientAPI.Branch.getBranches();

	if (response.error) {
		return thunkAPI.rejectWithValue(response.error);
	}

	return response as BranchProperties[];
});

export const getClientBranches = createAsyncThunk('client/branches', async (clientId: string, thunkAPI) => {
	const response = await API.ClientAPI.BranchClient.getBranches(clientId);

	if (response.error) {
		return thunkAPI.rejectWithValue(response.error);
	}

	return response as BranchProperties[];
});

export const addClientToBranches = createAsyncThunk(
	'clients/client/add',
	async (data: ClientBranchProperties, thunkAPI) => {
		const response = await API.ClientAPI.BranchClient.addClientToBranch(data);

		if (response.error) {
			return thunkAPI.rejectWithValue(response.error);
		}

		return response;
	},
);

interface BranchInitialStateProperties {
	branches: BranchProperties[];
	clientBranches: BranchProperties[];
	error: ErrorProperties;

	loading: boolean;
}

const initialBranchState: BranchInitialStateProperties = {
	loading: false,
	branches: [],
	clientBranches: [],
	error: {
		status: 0,
		title: '',
	},
};

const branchSlice = createSlice({
	name: 'branch',
	initialState: initialBranchState,
	reducers: {},
	extraReducers: (builder) => {
		// getBranches
		builder.addCase(getBranches.pending, (state) => {
			state.loading = true;
			state.error = initialBranchState.error;
		});
		builder.addCase(getBranches.fulfilled, (state, action) => {
			state.loading = false;
			state.branches = action.payload;
		});
		builder.addCase(getBranches.rejected, (state, action) => {
			state.loading = false;
			state.error = action.payload as ErrorProperties;
		});

		// getClientBranches
		builder.addCase(getClientBranches.pending, (state) => {
			state.loading = true;
			state.error = initialBranchState.error;
		});
		builder.addCase(getClientBranches.fulfilled, (state, action) => {
			state.loading = false;
			state.clientBranches = action.payload;
		});
		builder.addCase(getClientBranches.rejected, (state, action) => {
			state.loading = false;
			state.error = action.payload as ErrorProperties;
		});

		// addClientToBranches
		builder.addCase(addClientToBranches.pending, (state) => {
			state.loading = true;
			state.error = initialBranchState.error;
		});
		builder.addCase(addClientToBranches.fulfilled, (state) => {
			state.loading = false;
		});
		builder.addCase(addClientToBranches.rejected, (state, action) => {
			state.loading = false;
			state.error = action.payload as ErrorProperties;
		});
		// reset state
		builder.addCase(resetState, () => initialBranchState);
	},
});

export default branchSlice.reducer;
